import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { ZONES } from '@/lib/quizData';
import { Input } from '@/components/ui/input';
import { ArrowLeft, ShieldCheck, Plus, Trash2, Save, GraduationCap, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const ZONE_LIST = Object.values(ZONES);

export default function AdminCourses() {
  const navigate = useNavigate();
  const [coursesByZone, setCoursesByZone] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [activeZone, setActiveZone] = useState(ZONE_LIST[0].id);

  useEffect(() => {
    if (!localStorage.getItem('cq_admin_token')) { navigate('/admin/login'); return; }
    (async () => {
      try {
        const res = await api.get('/admin/courses');
        const init = {};
        ZONE_LIST.forEach((z) => {
          init[z.id] = res.data.courses[z.id] || [];
        });
        setCoursesByZone(init);
      } catch (e) {
        if (e?.response?.status === 401) { localStorage.removeItem('cq_admin_token'); navigate('/admin/login'); }
        else toast.error('Could not load courses');
      } finally { setLoading(false); }
    })();
  }, [navigate]);

  const updateCourse = (zoneId, idx, field, value) => {
    setCoursesByZone((s) => {
      const list = [...(s[zoneId] || [])];
      list[idx] = { ...list[idx], [field]: value };
      return { ...s, [zoneId]: list };
    });
  };

  const addCourse = (zoneId) => {
    setCoursesByZone((s) => ({ ...s, [zoneId]: [...(s[zoneId] || []), { title: '', provider: 'Coursera', url: '' }] }));
  };

  const removeCourse = (zoneId, idx) => {
    setCoursesByZone((s) => ({ ...s, [zoneId]: (s[zoneId] || []).filter((_, i) => i !== idx) }));
  };

  const saveZone = async (zoneId) => {
    const courses = (coursesByZone[zoneId] || []).filter((c) => c.title && c.url);
    setSaving((s) => ({ ...s, [zoneId]: true }));
    try {
      await api.put('/admin/courses', { zone_id: zoneId, courses });
      toast.success(`Saved ${courses.length} course${courses.length === 1 ? '' : 's'} for ${ZONES[zoneId].name}`);
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving((s) => ({ ...s, [zoneId]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <nav className="sticky top-0 z-30 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/admin" data-testid="courses-back-link" className="flex items-center gap-2 text-sm font-bold text-muted-ink hover:text-ink">
            <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Back to leads
          </Link>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#0055FF]" strokeWidth={2.5} />
            <div className="font-display font-extrabold text-ink text-sm">Coursera Affiliate Manager</div>
          </div>
          <div className="w-20" />
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-5 py-8 space-y-6">
        <div>
          <h1 className="font-display font-black text-ink text-3xl sm:text-4xl tracking-tight">Recommended Courses</h1>
          <p className="text-sm text-muted-ink mt-1">Manage Coursera (or any) course links per Career Zone. These appear on every student's result page. Paste your affiliate URLs here.</p>
        </div>

        {loading ? (
          <div className="card-soft p-10 text-center text-muted-ink">Loading…</div>
        ) : (
          <>
            {/* Zone tabs */}
            <div className="flex flex-wrap gap-2">
              {ZONE_LIST.map((z) => (
                <button
                  key={z.id}
                  data-testid={`courses-tab-${z.id}`}
                  onClick={() => setActiveZone(z.id)}
                  className={`px-4 py-2 rounded-full text-xs font-display font-extrabold transition-colors border-2`}
                  style={{
                    background: activeZone === z.id ? z.color : '#fff',
                    color: activeZone === z.id ? '#fff' : '#0F172A',
                    borderColor: activeZone === z.id ? z.color : '#E2E8F0',
                  }}
                >
                  {z.name}
                </button>
              ))}
            </div>

            {/* Active zone editor */}
            {ZONE_LIST.filter((z) => z.id === activeZone).map((z) => (
              <div key={z.id} className="card-soft p-6 sm:p-8">
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white" style={{ background: z.color }}>
                      <GraduationCap strokeWidth={2.5} />
                    </div>
                    <div>
                      <div className="font-display font-extrabold text-ink text-lg">{z.name}</div>
                      <div className="text-xs text-muted-ink">{(coursesByZone[z.id] || []).length} course{(coursesByZone[z.id] || []).length === 1 ? '' : 's'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => addCourse(z.id)} data-testid={`add-course-${z.id}`} className="btn-secondary inline-flex items-center gap-1.5 !py-2 !px-3 text-xs">
                      <Plus className="w-3.5 h-3.5" strokeWidth={3} /> Add
                    </button>
                    <button onClick={() => saveZone(z.id)} data-testid={`save-courses-${z.id}`} disabled={saving[z.id]} className="btn-primary inline-flex items-center gap-1.5 !py-2 !px-3 text-xs disabled:opacity-60">
                      <Save className="w-3.5 h-3.5" strokeWidth={3} /> {saving[z.id] ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {(coursesByZone[z.id] || []).length === 0 && (
                    <div className="text-center py-8 text-muted-ink text-sm">No courses yet. Click "Add" to create one.</div>
                  )}
                  {(coursesByZone[z.id] || []).map((c, idx) => (
                    <div key={c._rid || `${z.id}-${idx}`} className="grid sm:grid-cols-12 gap-2 items-center bg-[#F8FAFC] rounded-2xl p-3">
                      <Input
                        data-testid={`course-title-${z.id}-${idx}`}
                        value={c.title}
                        onChange={(e) => updateCourse(z.id, idx, 'title', e.target.value)}
                        placeholder="Course title"
                        className="sm:col-span-4 h-10 rounded-xl bg-white"
                      />
                      <Input
                        data-testid={`course-provider-${z.id}-${idx}`}
                        value={c.provider || ''}
                        onChange={(e) => updateCourse(z.id, idx, 'provider', e.target.value)}
                        placeholder="Coursera"
                        className="sm:col-span-2 h-10 rounded-xl bg-white"
                      />
                      <Input
                        data-testid={`course-url-${z.id}-${idx}`}
                        value={c.url}
                        onChange={(e) => updateCourse(z.id, idx, 'url', e.target.value)}
                        placeholder="https://www.coursera.org/learn/... (affiliate URL)"
                        className="sm:col-span-5 h-10 rounded-xl bg-white text-xs"
                      />
                      <div className="sm:col-span-1 flex items-center justify-end gap-1">
                        {c.url && (
                          <a href={c.url} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-[#E2E8F0] hover:bg-[#CBD5E1] flex items-center justify-center text-muted-ink">
                            <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.5} />
                          </a>
                        )}
                        <button data-testid={`delete-course-${z.id}-${idx}`} onClick={() => removeCourse(z.id, idx)} className="w-8 h-8 rounded-lg bg-[#FEE2E2] hover:bg-[#FECACA] text-[#DC2626] flex items-center justify-center">
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
